import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

export default function Departments() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", description: "", head: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [d, e] = await Promise.all([
                api.get("/departments"),
                api.get("/employees"),
            ]);
            setItems(d.data);
            const c = {};
            e.data.forEach((emp) => {
                if (emp.department) c[emp.department] = (c[emp.department] || 0) + 1;
            });
            setCounts(c);
        } catch (e) {
            toast.error("Failed to load departments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onNew = () => { setEditing(null); setForm({ name: "", description: "", head: "" }); setOpen(true); };
    const onEdit = (d) => { setEditing(d); setForm({ name: d.name, description: d.description || "", head: d.head || "" }); setOpen(true); };

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/departments/${editing.id}`, form);
                toast.success("Department updated");
            } else {
                await api.post("/departments", form);
                toast.success("Department added");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (d) => {
        if (!window.confirm(`Delete "${d.name}"?`)) return;
        try {
            await api.delete(`/departments/${d.id}`);
            toast.success("Department deleted");
            load();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="departments-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Organization</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Departments
                    </h1>
                </div>
                <button
                    onClick={onNew}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    data-testid="add-department-button"
                >
                    <Plus className="w-4 h-4" /> New department
                </button>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                {loading ? (
                    <div className="bg-white p-10 col-span-full text-sm text-ink/40">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="bg-white p-10 col-span-full text-sm text-ink/50">
                        No departments yet. Create one to start organizing your team.
                    </div>
                ) : (
                    items.map((d) => (
                        <div key={d.id} className="bg-white p-8 hover:bg-bone transition-colors" data-testid={`dept-card-${d.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                    <div className="overline">{counts[d.name] || 0} people</div>
                                    <h3 className="mt-2 font-display font-bold text-xl text-ink tracking-tight truncate">
                                        {d.name}
                                    </h3>
                                    {d.head && <div className="mt-1 text-sm text-ink/60">Lead · {d.head}</div>}
                                    {d.description && (
                                        <p className="mt-3 text-sm text-ink/60 leading-relaxed line-clamp-3">
                                            {d.description}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        onClick={() => onEdit(d)}
                                        className="p-2 text-ink/60 hover:text-klein hover:bg-klein/5"
                                        data-testid={`dept-edit-${d.id}`}
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(d)}
                                        className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                        data-testid={`dept-delete-${d.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="department-dialog">
                    <form
                        onSubmit={onSave}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-ink/10 w-full max-w-lg animate-fade-in-up"
                    >
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <h3 className="font-display font-bold text-xl">{editing ? "Edit department" : "New department"}</h3>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5" data-testid="department-dialog-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Name *</label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="dept-name-input"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">Head / Lead</label>
                                <input
                                    value={form.head}
                                    onChange={(e) => setForm({ ...form, head: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="dept-head-input"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">Description</label>
                                <textarea
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none resize-none"
                                    data-testid="dept-description-input"
                                />
                            </div>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5" data-testid="dept-cancel-button">
                                Cancel
                            </button>
                            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="dept-save-button">
                                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
